import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Send, ExternalLink, Copy, Eye, RefreshCw, MessageSquareText, Gift, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import type { Perk } from "@/components/proposal/perks";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

const STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  sent: "Sent",
  viewed: "Viewed",
  accepted: "Accepted",
  rejected: "Rejected",
};

const STATUS_COLORS: Record<string, string> = {
  draft: "bg-zinc-100 text-zinc-700",
  sent: "bg-blue-100 text-blue-700",
  viewed: "bg-amber-100 text-amber-800",
  accepted: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-700",
};

export function ProposalEditPage() {
  const { id } = useParams();
  const utils = trpc.useUtils();
  const [sendOpen, setSendOpen] = useState(false);
  const [sendEmail, setSendEmail] = useState("");
  const [sendSubject, setSendSubject] = useState("");
  const [sendIntro, setSendIntro] = useState("");

  const proposal = trpc.proposals.get.useQuery({ id: id! }, { enabled: !!id });

  const send = trpc.proposals.send.useMutation({
    onSuccess: () => {
      toast.success("Proposal sent");
      setSendOpen(false);
      utils.proposals.get.invalidate({ id: id! });
    },
    onError: (e) => toast.error(e.message ?? "Failed to send"),
  });

  const updateProposal = trpc.proposals.update.useMutation({
    onMutate: async (vars) => {
      await utils.proposals.get.cancel({ id: id! });
      const prev = utils.proposals.get.getData({ id: id! });
      if (prev && (vars.patch.landingFeatures || vars.patch.perks)) {
        utils.proposals.get.setData(
          { id: id! },
          {
            ...prev,
            ...(vars.patch.landingFeatures
              ? {
                  landingFeatures: {
                    ...(prev.landingFeatures ?? {}),
                    ...vars.patch.landingFeatures,
                  },
                }
              : {}),
            ...(vars.patch.perks ? { perks: vars.patch.perks } : {}),
          }
        );
      }
      return { prev };
    },
    onError: (e, _vars, ctx) => {
      if (ctx?.prev) utils.proposals.get.setData({ id: id! }, ctx.prev);
      toast.error(e.message ?? "Failed to save");
    },
    onSuccess: () => {
      utils.proposals.get.invalidate({ id: id! });
    },
  });

  if (proposal.isLoading) {
    return (
      <div className="mx-auto max-w-screen-xl space-y-4 px-6 py-6">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!proposal.data) {
    return (
      <div className="mx-auto max-w-screen-xl px-6 py-6">
        <Button variant="ghost" asChild>
          <Link to="/"><ArrowLeft className="h-4 w-4" />Back</Link>
        </Button>
        <p className="mt-4 text-muted-foreground">Proposal not found.</p>
      </div>
    );
  }

  const p = proposal.data;
  const slug = (p as { opportunityPublicSlug?: string | null }).opportunityPublicSlug;
  const publicUrl = slug ? `/p/${slug}` : `/p/${p.publicToken}`;
  const fullPublicUrl = `${window.location.origin}${publicUrl}`;
  const perks: Perk[] = ((p as { perks?: Perk[] | null }).perks ?? []) as Perk[];

  const savePerks = (next: Perk[]) => {
    updateProposal.mutate({ id: id!, patch: { perks: next } });
  };

  const addPerk = () => {
    const newPerk: Perk = {
      id: `perk_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      enabled: true,
      title: "€2 000 kreditas pasirinkus metinį planą",
      description:
        "Kreditas skiriamas funkcijoms, kurių Jums reikia — individualioms integracijoms, papildomoms automatizacijoms ar mokymams. Naudojama per pirmus 12 mėn.",
      amount: "€2 000",
    };
    savePerks([...perks, newPerk]);
  };

  const updatePerk = (perkId: string, patch: Partial<Perk>) => {
    savePerks(perks.map((perk) => (perk.id === perkId ? { ...perk, ...patch } : perk)));
  };

  const removePerk = (perkId: string) => {
    savePerks(perks.filter((perk) => perk.id !== perkId));
  };

  const initSend = () => {
    setSendEmail((p.variables as Record<string, unknown>)?.client
      ? ((p.variables as Record<string, unknown>).client as Record<string, string>)?.email ?? ""
      : "");
    setSendSubject("");
    setSendIntro("");
    setSendOpen(true);
  };

  return (
    <div className="mx-auto max-w-screen-xl px-4 py-6 sm:px-6">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <Button variant="ghost" size="sm" asChild className="-ml-3 mb-1">
            <Link to={`/o/${p.opportunityId}`}><ArrowLeft className="h-4 w-4" />Opportunity</Link>
          </Button>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">Proposal</h1>
            <span className={cn("rounded-full px-3 py-1 text-xs font-semibold", STATUS_COLORS[p.status] ?? "")}>
              {STATUS_LABELS[p.status] ?? p.status}
            </span>
            {p.viewCount > 0 && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Eye className="h-3 w-3" />
                {p.viewCount} views
              </span>
            )}
          </div>
        </div>
        <div className="grid w-full grid-cols-2 items-center gap-2 sm:flex sm:w-auto sm:flex-wrap">
          <Button variant="outline" size="sm" asChild className="w-full sm:w-auto">
            <a href={publicUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4" />
              Preview
            </a>
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="w-full sm:w-auto"
            onClick={() => {
              navigator.clipboard.writeText(fullPublicUrl);
              toast.success("Link copied");
            }}
          >
            <Copy className="h-4 w-4" />
            Copy link
          </Button>
          <Button
            size="sm"
            onClick={initSend}
            disabled={p.status === "accepted"}
            className="col-span-2 w-full sm:col-span-1 sm:w-auto"
          >
            <Send className="h-4 w-4" />
            Send to client
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        {/* Preview */}
        <Card className="overflow-hidden">
          <CardHeader className="border-b py-3">
            <CardTitle className="text-sm">Public link</CardTitle>
          </CardHeader>
          <CardContent className="p-6 text-sm text-muted-foreground">
            <p>The customer-facing proposal lives at:</p>
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-block break-all font-mono text-xs text-foreground underline underline-offset-4"
            >
              {fullPublicUrl}
            </a>
            <p className="mt-4 text-xs">
              The page renders entirely from React with the brand sections + body data on this proposal.
            </p>
          </CardContent>
        </Card>

        {/* Sidebar info */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Public link</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 rounded-md bg-muted p-2">
                <code className="flex-1 break-all text-xs">{fullPublicUrl}</code>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Landing page features
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2.5">
                  <MessageSquareText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div>
                    <Label
                      htmlFor="feature-feedback-form"
                      className="text-sm font-medium leading-tight"
                    >
                      Client feedback form
                    </Label>
                    <p className="mt-0.5 text-[11.5px] leading-relaxed text-muted-foreground">
                      Form shown at the bottom — comments go into the opportunity notes tagged "client".
                    </p>
                  </div>
                </div>
                <Switch
                  id="feature-feedback-form"
                  checked={(p.landingFeatures?.feedbackForm ?? true) !== false}
                  disabled={updateProposal.isPending}
                  onCheckedChange={(checked) =>
                    updateProposal.mutate({
                      id: id!,
                      patch: {
                        landingFeatures: {
                          ...(p.landingFeatures ?? {}),
                          feedbackForm: checked,
                        },
                      },
                    })
                  }
                />
              </div>
              <p className="text-[10.5px] leading-relaxed text-muted-foreground">
                Features show above the proposal HTML template and persist even after switching templates.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between gap-3 space-y-0">
              <div className="flex items-center gap-2">
                <Gift className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Perks
                </CardTitle>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={addPerk}
                disabled={updateProposal.isPending}
              >
                <Plus className="h-3.5 w-3.5" />
                Add
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {perks.length === 0 && (
                <p className="text-[12px] leading-relaxed text-muted-foreground">
                  No perks yet. Add a block (e.g. "€2 000 credit for annual plan") that shows below the calculator totals on the public proposal.
                </p>
              )}
              {perks.map((perk) => (
                <div
                  key={perk.id}
                  className={cn(
                    "space-y-2 rounded-lg border p-3 transition-colors",
                    perk.enabled ? "border-border bg-background" : "border-dashed border-border bg-muted/30"
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={perk.enabled}
                        disabled={updateProposal.isPending}
                        onCheckedChange={(checked) =>
                          updatePerk(perk.id, { enabled: checked })
                        }
                      />
                      <span className="text-[11px] font-medium text-muted-foreground">
                        {perk.enabled ? "Visible to client" : "Hidden"}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => removePerk(perk.id)}
                      disabled={updateProposal.isPending}
                      aria-label="Delete perk"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-medium text-muted-foreground">
                      Title
                    </Label>
                    <Input
                      value={perk.title}
                      placeholder="€2 000 credit for annual plan"
                      onChange={(e) => updatePerk(perk.id, { title: e.target.value })}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-medium text-muted-foreground">
                      Description
                    </Label>
                    <Textarea
                      value={perk.description}
                      placeholder="Credit applied to integrations, automations, or trainings — used during the first 12 months."
                      onChange={(e) => updatePerk(perk.id, { description: e.target.value })}
                      className="min-h-[60px] text-sm"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-medium text-muted-foreground">
                      Amount label
                    </Label>
                    <Input
                      value={perk.amount}
                      placeholder="€2 000"
                      onChange={(e) => updatePerk(perk.id, { amount: e.target.value })}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
              ))}
              <p className="text-[10.5px] leading-relaxed text-muted-foreground">
                Perks render below the totals on the public proposal page. Toggle each one on/off without deleting it.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Stats</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Views</span>
                <span className="font-medium">{p.viewCount}</span>
              </div>
              {p.sentAt && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Sent</span>
                  <span className="font-medium">{new Date(p.sentAt).toLocaleDateString("en-US")}</span>
                </div>
              )}
              {p.firstViewedAt && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">First viewed</span>
                  <span className="font-medium">{new Date(p.firstViewedAt).toLocaleDateString("en-US")}</span>
                </div>
              )}
              {p.acceptedAt && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Accepted</span>
                  <span className="font-medium text-emerald-600">{new Date(p.acceptedAt).toLocaleDateString("en-US")}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Send dialog */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send proposal</DialogTitle>
            <DialogDescription>The proposal link will be sent to the client by email.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Recipient email</Label>
              <Input
                value={sendEmail}
                onChange={(e) => setSendEmail(e.target.value)}
                placeholder="client@company.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Subject (optional)</Label>
              <Input
                value={sendSubject}
                onChange={(e) => setSendSubject(e.target.value)}
                placeholder="Noium proposal — Your company"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Short message (optional)</Label>
              <Textarea
                value={sendIntro}
                onChange={(e) => setSendIntro(e.target.value)}
                placeholder="Hi, sending you our commercial proposal..."
                className="mt-1 min-h-[80px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)}>Cancel</Button>
            <Button
              disabled={!sendEmail || send.isPending}
              onClick={() =>
                send.mutate({
                  id: id!,
                  email: sendEmail,
                  subject: sendSubject || undefined,
                  intro: sendIntro || undefined,
                })
              }
            >
              {send.isPending ? "Sending..." : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
